import React, { useState } from "react";

interface AddEventDialogProps {
  onClose: () => void;
  onAdd: (event: {
    title: string;
    date_start: string;
    city: string;
    description?: string;
    organizer?: string;
    contact?: string;
  }) => Promise<void>;
}

export const AddEventDialog: React.FC<AddEventDialogProps> = ({ onClose, onAdd }) => {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [organizer, setOrganizer] = useState("");
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title || !city) return;
    setSubmitting(true);
    try {
      await onAdd({
        title,
        date_start: date || undefined,
        city,
        description: description || undefined,
        organizer: organizer || undefined,
        contact: contact || undefined,
      } as any);
    } finally {
      setSubmitting(false);
    }
  };

  /* ——— Label helper ——— */
  const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <label className="eyebrow-label" style={{ display: "block", marginBottom: "0.375rem" }}>
      {children}
    </label>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(5,5,5,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        padding: "1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="animate-fade-in-up"
        style={{
          width: "100%",
          maxWidth: "560px",
          borderRadius: "18px",
          padding: "2rem",
          background: "rgba(10,10,10,0.95)",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow:
            "0 32px 64px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1.5rem",
          }}
        >
          <h2
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: "1.125rem",
              letterSpacing: "-0.03em",
              color: "#fafafa",
              margin: 0,
            }}
          >
            Přidat akci ručně
          </h2>
          <button
            onClick={onClose}
            aria-label="Zavřít"
            style={{
              width: "1.75rem",
              height: "1.75rem",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.05)",
              color: "#666",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1rem",
              lineHeight: 1,
              transition: "all 0.15s ease",
              padding: 0,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = "#fafafa";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.25)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = "#666";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.1)";
            }}
          >
            ×
          </button>
        </div>

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <FieldLabel>Název akce *</FieldLabel>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="dwx-input"
              placeholder="Hasičská soutěž Přibyslav"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <FieldLabel>Datum</FieldLabel>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="dwx-input"
              />
            </div>
            <div>
              <FieldLabel>Město *</FieldLabel>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="dwx-input"
                placeholder="Žďár nad Sázavou"
              />
            </div>
          </div>

          <div>
            <FieldLabel>Popis</FieldLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="dwx-input"
              placeholder="O čem akce je..."
              style={{ resize: "none" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <FieldLabel>Pořadatel</FieldLabel>
              <input
                value={organizer}
                onChange={(e) => setOrganizer(e.target.value)}
                className="dwx-input"
              />
            </div>
            <div>
              <FieldLabel>Kontakt</FieldLabel>
              <input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="dwx-input"
                placeholder="email nebo telefon"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            marginTop: "1.75rem",
          }}
        >
          <button
            onClick={onClose}
            className="btn-secondary-hero"
            style={{ flex: 1, justifyContent: "center" }}
          >
            Zrušit
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title || !city || submitting}
            className="btn-primary-hero"
            style={{
              flex: 1,
              justifyContent: "center",
              opacity: !title || !city || submitting ? 0.5 : 1,
              cursor: !title || !city || submitting ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? (
              <>
                <span
                  className="dwx-spinner"
                  style={{ width: "1rem", height: "1rem", borderWidth: "2px" }}
                />
                Přidávám...
              </>
            ) : (
              "Přidat akci"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
